;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { zodResolver } from '@hookform/resolvers/zod';
import {
  toAnalyticsReasonCode,
  trackFeatureUsed,
  trackWalletAction,
} from '@workspace/analytics/posthog/events';
import {
  AmountInput,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@workspace/ui';
import { formatAmount, formatConversionRate } from '@workspace/ui/lib';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';
import { useConvertCurrencies, useCurrencies } from '@/hooks/use-currencies';
import { useUserVerification } from '@/hooks/use-user';
import { useConvert, useWallets } from '@/hooks/use-wallet';
import { ALLOWED_WALLET_CURRENCY_CODES } from '@/lib/constants';

interface CurrencyConversionDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback function called when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
}

/**
 * Creates a Zod schema for the currency conversion form with dynamic validation
 * based on the available balance.
 *
 * @param availableBalance - The available balance in the source wallet as a string
 * @returns A Zod schema object for form validation
 */
function createFormSchema(availableBalance: string) {
  return z.object({
    fromAmount: z.object({
      value: z
        .string()
        .min(1, 'Required')
        .refine(
          (val) => {
            // Allow empty string during input (validation happens on blur)
            if (val === '') return true;

            // Ensure the amount doesn't exceed available balance
            return Number(val) <= Number(availableBalance);
          },
          {
            message: 'Insufficient funds',
          }
        )
        .refine((val) => Number(val) > 0, {
          message: 'Required',
        }),
      currency: z.string().min(1, 'Required'),
    }),
    toAmount: z.object({
      value: z
        .string()
        .min(1, 'Required')
        .refine((val) => Number(val) > 0, {
          message: 'Required',
        }),
      currency: z.string().min(1, 'Required'),
    }),
  });
}

/**
 * Extracts the currency code from a formatted currency value string.
 * The currency value is expected to be in the format "CODE-NAME" (e.g., "USD-US Dollar").
 *
 * @param value - The formatted currency string (e.g., "USD-US Dollar")
 * @returns The currency code (e.g., "USD") or an empty string if not found
 */
function getCurrencyCodeFromValue(value: string): string {
  return value.split('-')[0] || '';
}

/**
 * CurrencyConversionDialog Component
 *
 * A dialog component that allows users to convert currency between different wallets.
 * It provides a form with two amount inputs (from and to) with automatic conversion
 * rate calculation and bidirectional amount conversion based on which field is being edited.
 *
 * Features:
 * - Real-time conversion rate calculation
 * - Automatic amount conversion when editing either field
 * - Balance validation to prevent over-spending
 * - Currency selection from available wallets
 *
 * @param props - The component props
 * @param props.open - Whether the dialog is currently open
 * @param props.onOpenChange - Callback function when dialog open state changes
 * @returns The rendered dialog component
 */
export function CurrencyConversionDialog({ open, onOpenChange }: CurrencyConversionDialogProps) {
  // Available balance in the selected source wallet
  const [availableBalance, setAvailableBalance] = useState<string>('0.00');

  const hasTrackedStartRef = useRef(false);

  useEffect(() => {
    if (!open) {
      hasTrackedStartRef.current = false;
      return;
    }
    if (hasTrackedStartRef.current) return;
    hasTrackedStartRef.current = true;
    trackFeatureUsed('wallet-app', 'currency_conversion');
    trackWalletAction('currency_conversion_started', {
      flow: 'currency_conversion',
      result: 'started',
    });
  }, [open]);

  // Get authenticated user's ID for wallet filtering
  const { data: userVerification } = useUserVerification(open);

  // Fetch available currencies and user wallets
  const { data: currencies } = useCurrencies();
  const { data: wallets } = useWallets(!!userVerification?.id, {
    userId: userVerification?.id,
  });

  // Mutation hook for calculating conversion rates
  const {
    mutate: calculateConversion,
    isPending: isCalculatingConversion,
    data: conversionData,
  } = useConvertCurrencies();

  // Mutation hook for executing the currency conversion
  const { mutate: convert, isPending: isConverting } = useConvert();

  // Create form schema with dynamic validation based on available balance
  const formSchema = createFormSchema(availableBalance || '0.00');

  type FormData = z.infer<typeof formSchema>;

  // Initialize react-hook-form with validation schema
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fromAmount: { value: '', currency: '' },
      toAmount: { value: '', currency: '' },
    },
    mode: 'onTouched', // Validate on blur/touch for better UX
  });

  // Current conversion rate between selected currencies
  const [conversionRate, setConversionRate] = useState<number | null>(null);

  // Track which field is currently being edited to determine conversion direction
  const editingFieldRef = useRef<'fromAmount' | 'toAmount' | null>(null);

  // Filter to only default wallets to prevent selecting pre_payment wallets
  const defaultWallets = useMemo(
    () => wallets?.filter((wallet) => wallet.type === 'default') ?? [],
    [wallets]
  );

  // Transform wallets and currencies into options format for the currency selector
  // Each option includes name, code, and flag image URL
  const walletCurrencyOptions = useMemo(() => {
    if (!defaultWallets.length || !currencies) return [];

    return (
      defaultWallets
        ?.map((wallet) => {
          const currency = currencies.find((currency) => currency.id === wallet.currency_id);
          if (currency) {
            return {
              name: currency.name,
              code: currency.code,
              flag: `https://i.nativeteams.com/assets/app/flags_code/${currency.flag_code}.svg`,
            };
          }
          return null;
        })
        ?.filter((item) => item !== null) || []
    );
  }, [defaultWallets, currencies]);

  const allowedCurrencyOptions = useMemo(() => {
    if (!currencies) return [];

    return currencies
      .filter((currency) => ALLOWED_WALLET_CURRENCY_CODES.includes(currency.code))
      .map((currency) => ({
        name: currency.name,
        code: currency.code,
        flag: `https://i.nativeteams.com/assets/app/flags_code/${currency.flag_code}.svg`,
      }));
  }, [currencies]);

  // Watch form values for reactive updates
  const selectedFromCurrency = useWatch({ control: form.control, name: 'fromAmount.currency' });
  const selectedToCurrency = useWatch({ control: form.control, name: 'toAmount.currency' });
  const selectedFromAmount = useWatch({ control: form.control, name: 'fromAmount.value' });
  const selectedToAmount = useWatch({ control: form.control, name: 'toAmount.value' });

  // Extract currency codes from formatted currency strings (format: "CODE-NAME")
  const fromCurrencyCode = getCurrencyCodeFromValue(selectedFromCurrency);
  const toCurrencyCode = getCurrencyCodeFromValue(selectedToCurrency);

  // Find the default wallet corresponding to the selected source currency
  const selectedWallet = useMemo(() => {
    const currency = currencies?.find(
      (currency) => currency.code === getCurrencyCodeFromValue(selectedFromCurrency)
    );
    return defaultWallets.find((wallet) => wallet.currency_id === currency?.id) || null;
  }, [defaultWallets, currencies, selectedFromCurrency]);

  // Initialize form with default currencies when dialog opens
  // Sets the first wallet as "from" currency and tries to find a different currency for "to"
  useEffect(() => {
    if (open && defaultWallets.length > 0 && currencies) {
      const firstWallet = defaultWallets[0];
      if (firstWallet) {
        const firstCurrency = currencies.find(
          (currency) => currency.id === firstWallet.currency_id
        );
        if (firstCurrency) {
          // Try to find a second wallet with a different currency
          const secondWallet = defaultWallets.length > 1 ? defaultWallets[1] : defaultWallets[0];
          if (secondWallet) {
            const secondCurrency = currencies.find(
              (currency) => currency.id === secondWallet.currency_id
            );
            // Find an allowed currency different from the first one, or use the second wallet's currency
            const toCurrency =
              secondCurrency &&
              secondCurrency.code !== firstCurrency.code &&
              ALLOWED_WALLET_CURRENCY_CODES.includes(secondCurrency.code)
                ? secondCurrency
                : currencies.find(
                    (currency) =>
                      ALLOWED_WALLET_CURRENCY_CODES.includes(currency.code) &&
                      currency.code !== firstCurrency.code
                  );

            // Reset form with default currency selections
            form.reset({
              fromAmount: { value: '', currency: `${firstCurrency.code}-${firstCurrency.name}` },
              toAmount: {
                value: '',
                currency: toCurrency?.code ? `${toCurrency?.code}-${toCurrency?.name}` : '',
              },
            });
          }
        }
      }
    }
  }, [open, defaultWallets, currencies, form]);

  // Update available balance and fetch conversion rate when currencies change
  useEffect(() => {
    // Update available balance from selected wallet
    setAvailableBalance(selectedWallet?.available_balance || '0.00');

    // Clear validation errors on next frame to prevent stale errors
    requestAnimationFrame(() => form.clearErrors('fromAmount.value'));

    // Reset conversion rate if currencies are not selected
    if (!fromCurrencyCode || !toCurrencyCode) {
      setConversionRate(null);
      return;
    }

    // Same currency conversion rate is always 1
    if (fromCurrencyCode === toCurrencyCode) {
      setConversionRate(1);
      return;
    }

    // Fetch conversion rate for 1 unit of source currency
    calculateConversion({
      amount: 1,
      from: fromCurrencyCode,
      to: toCurrencyCode,
    });
  }, [fromCurrencyCode, toCurrencyCode, calculateConversion, selectedWallet, form]);

  // Update conversion rate when conversion calculation completes
  useEffect(() => {
    if (conversionData?.converted.amount) {
      setConversionRate(Number(conversionData.converted.amount));
    }
  }, [conversionData]);

  // Auto-convert "to" amount when "from" amount is being edited
  // Only updates if the user is actively editing the "from" field to prevent circular updates
  useEffect(() => {
    if (editingFieldRef.current === 'fromAmount' && conversionRate !== null && conversionRate > 0) {
      const fromAmountNum = Number(selectedFromAmount);
      if (!Number.isNaN(fromAmountNum) && fromAmountNum > 0) {
        // Calculate converted amount: fromAmount * conversionRate
        const toAmount = fromAmountNum * conversionRate;
        form.setValue(
          'toAmount',
          {
            value: String(toAmount),
            currency: selectedToCurrency,
          },
          { shouldValidate: false } // Skip validation to prevent errors during typing
        );
      } else {
        // Clear "to" amount if "from" amount is invalid or empty
        form.setValue(
          'toAmount',
          {
            value: '',
            currency: selectedToCurrency,
          },
          { shouldValidate: false }
        );
      }
    }
  }, [selectedFromAmount, conversionRate, form, selectedToCurrency]);

  // Auto-convert "from" amount when "to" amount is being edited
  // Only updates if the user is actively editing the "to" field to prevent circular updates
  useEffect(() => {
    if (editingFieldRef.current === 'toAmount' && conversionRate !== null && conversionRate > 0) {
      const toAmountNum = Number(selectedToAmount);
      if (!Number.isNaN(toAmountNum) && toAmountNum > 0) {
        // Calculate source amount: toAmount / conversionRate
        const fromAmount = toAmountNum / conversionRate;
        form.setValue(
          'fromAmount',
          {
            value: String(fromAmount),
            currency: selectedFromCurrency,
          },
          { shouldValidate: false } // Skip validation to prevent errors during typing
        );
      } else {
        // Clear "from" amount if "to" amount is invalid or empty
        form.setValue(
          'fromAmount',
          {
            value: '',
            currency: selectedFromCurrency,
          },
          { shouldValidate: false }
        );
      }
    }
  }, [selectedToAmount, conversionRate, form, selectedFromCurrency]);

  // Filter out the selected "from" currency from "to" currency options
  // Prevents users from selecting the same currency for both fields
  const toCurrencyOptions = useMemo(() => {
    if (!getCurrencyCodeFromValue(selectedFromCurrency)) return allowedCurrencyOptions;
    return (
      allowedCurrencyOptions.filter(
        (currency) => currency.code !== getCurrencyCodeFromValue(selectedFromCurrency)
      ) || []
    );
  }, [allowedCurrencyOptions, selectedFromCurrency]);

  // Auto-switch "to" currency if user selects the same currency as "from"
  // Prevents invalid same-currency conversions
  useEffect(() => {
    if (selectedFromCurrency && selectedToCurrency === selectedFromCurrency && currencies) {
      // Find an available currency different from the selected "from" currency
      const availableCurrency = currencies.find(
        (currency) =>
          ALLOWED_WALLET_CURRENCY_CODES.includes(currency.code) &&
          currency.code !== getCurrencyCodeFromValue(selectedFromCurrency)
      );

      if (availableCurrency) {
        // Auto-select the first available different currency
        form.setValue('toAmount', {
          value: String(Number(selectedFromAmount) * (conversionRate || 1)) || '',
          currency: `${availableCurrency.code}-${availableCurrency.name}`,
        });
      } else {
        // Clear "to" currency if no alternative is available
        form.setValue('toAmount.currency', '');
      }
    }
  }, [
    currencies,
    selectedFromCurrency,
    selectedToCurrency,
    form,
    conversionRate,
    selectedFromAmount,
  ]);

  /**
   * Handles form submission for currency conversion.
   * Validates wallet existence and executes the conversion mutation.
   *
   * @param data - The validated form data containing from/to amounts and currencies
   */
  function handleSubmit(data: FormData) {
    try {
      // Find currency objects from the selected currency codes
      const fromCurrency = currencies?.find(
        (currency) => currency.code === getCurrencyCodeFromValue(data.fromAmount.currency)
      );
      const toCurrency = currencies?.find(
        (currency) => currency.code === getCurrencyCodeFromValue(data.toAmount.currency)
      );

      // Find corresponding default wallet for the selected source currency
      const fromWallet = defaultWallets.find((wallet) => wallet.currency_id === fromCurrency?.id);

      // Validate that source wallet and both currencies exist
      if (!fromWallet || !fromCurrency || !toCurrency) {
        throw new Error('Wallet not found');
      }

      // Execute the conversion mutation
      convert(
        {
          from_currency_id: fromWallet.currency_id,
          to_currency_id: toCurrency.id,
          amount: Number(data.fromAmount.value),
        },
        {
          onSuccess: () => {
            trackWalletAction('currency_conversion_submitted', {
              flow: 'currency_conversion',
              result: 'success',
              amount: Number(data.fromAmount.value),
              currency: fromCurrency.code,
              to_currency: toCurrency.code,
            });
            toast.success('Currency converted successfully');
            // Close dialog on successful conversion
            onOpenChange(false);
          },
          onError: (error) => {
            trackWalletAction('currency_conversion_submitted', {
              flow: 'currency_conversion',
              result: 'failure',
              amount: Number(data.fromAmount.value),
              currency: fromCurrency.code,
              to_currency: toCurrency.code,
              ...(error instanceof Error ? { reason_code: toAnalyticsReasonCode(error) } : {}),
            });
            // Display error message from API or generic error
            if (error instanceof Error) {
              toast.error(error.message);
            } else {
              toast.error('Failed to convert currency');
            }
          },
        }
      );
    } catch (error) {
      // Handle validation errors or other exceptions
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to convert currency');
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-grey-1000 text-[32px] leading-10 font-bold dark:text-neutral-100">
            Currency conversion
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            className="flex w-full flex-col space-y-7"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {/* Source currency and amount input */}
            <FormField
              control={form.control}
              name="fromAmount"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AmountInput
                      {...field}
                      onFocus={() => {
                        // Track that user is editing the "from" field for bidirectional conversion
                        editingFieldRef.current = 'fromAmount';
                      }}
                      onChange={field.onChange}
                      availableBalance={{
                        amount: formatAmount(selectedWallet?.available_balance || '0.00'),
                        currency: getCurrencyCodeFromValue(selectedFromCurrency),
                      }}
                      label="From"
                      currencies={walletCurrencyOptions}
                      fixedCurrency={false}
                      disabled={isCalculatingConversion}
                      inModal
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Display current conversion rate between selected currencies */}
            <p className="text-grey-800 text-base">
              Conversion rate:{' '}
              <span className="text-neutral-200">
                {isCalculatingConversion
                  ? 'Calculating...'
                  : conversionRate !== null && fromCurrencyCode && toCurrencyCode
                    ? `1 ${fromCurrencyCode} = ${formatConversionRate(conversionRate)} ${toCurrencyCode}`
                    : '—'}
              </span>
            </p>

            {/* Target currency and amount input */}
            <FormField
              control={form.control}
              name="toAmount"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AmountInput
                      {...field}
                      onFocus={() => {
                        // Track that user is editing the "to" field for bidirectional conversion
                        editingFieldRef.current = 'toAmount';
                      }}
                      onChange={field.onChange}
                      label="To"
                      currencies={toCurrencyOptions}
                      fixedCurrency={false}
                      disabled={isCalculatingConversion}
                      inModal
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit button - disabled during conversion calculations or execution */}
            <Button
              variant="primary"
              className="w-full"
              disabled={isCalculatingConversion}
              isLoading={isConverting}
              loadingText={'Converting'}
            >
              Convert
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
